import { createElement } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { renderHook, waitFor } from '@testing-library/react';
import { receivableInvoicesApiNamespace } from 'receivable-invoices-js/constants/hosts';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';
import { camelizeKeys } from 'receivable-invoices-js/react/utils/camelize-keys';
import {
  fetchReceivableInvoicesUploadKey,
  fetchReceivableInvoicesUpload,
  useFetchReceivableInvoicesUpload,
} from './fetch-receivable-invoices-upload';

vi.mock('receivable-invoices-js/react/utils/api-clients/qonto');
vi.mock('receivable-invoices-js/constants/hosts', () => ({
  receivableInvoicesApiNamespace: '/api/v5',
}));

vi.mock('@repo/design-system-kit', () => ({
  useToast: vi.fn(() => ({
    showToast: vi.fn(),
  })),
}));

describe('unit | API | queries | fetch-receivable-invoices-upload', () => {
  const mockQontoClient = vi.mocked(qontoClient);
  const uploadId = 'upload-123';
  const URL = `${receivableInvoicesApiNamespace}/receivable_invoices/uploads/shared`;

  beforeEach(() => {
    vi.clearAllMocks();
  });

  describe(fetchReceivableInvoicesUploadKey, () => {
    it('generates correct query key structure', () => {
      const result = fetchReceivableInvoicesUploadKey(uploadId);

      expect(result).toStrictEqual(['receivable-invoices-upload', uploadId]);
    });

    it('generates unique keys for different IDs', () => {
      const key1 = fetchReceivableInvoicesUploadKey('id-1');
      const key2 = fetchReceivableInvoicesUploadKey('id-2');

      expect(key1).not.toStrictEqual(key2);
    });
  });

  describe(fetchReceivableInvoicesUpload, () => {
    it('fetches upload by ID successfully', async () => {
      const apiResponse = {
        data: {
          id: uploadId,
          type: 'receivable_invoices_uploads',
          attributes: {
            url: 'https://example.com/upload.pdf',
            filename: 'attachment.pdf',
            src: null,
          },
        },
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchReceivableInvoicesUpload(uploadId);

      expect(mockQontoClient).toHaveBeenCalledWith(`${URL}/${uploadId}`);
      expect(result).toStrictEqual(
        camelizeKeys({
          ...apiResponse.data.attributes,
          id: uploadId,
        })
      );
    });

    it('returns camelized upload object', async () => {
      const apiResponse = {
        data: {
          id: uploadId,
          type: 'receivable_invoices_uploads',
          attributes: {
            url: 'https://example.com/terms.pdf',
            filename: 'terms-and-conditions.pdf',
            src: null,
          },
        },
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchReceivableInvoicesUpload(uploadId);

      expect(result).toHaveProperty('url', 'https://example.com/terms.pdf');
      expect(result).toHaveProperty('filename', 'terms-and-conditions.pdf');
      expect(result).toHaveProperty('id', uploadId);
    });

    it('handles upload with ArrayBuffer src', async () => {
      const arrayBuffer = new ArrayBuffer(8);
      const apiResponse = {
        data: {
          id: uploadId,
          type: 'receivable_invoices_uploads',
          attributes: {
            url: 'https://example.com/upload.pdf',
            filename: 'attachment.pdf',
            src: arrayBuffer,
          },
        },
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchReceivableInvoicesUpload(uploadId);

      expect(result).toHaveProperty('src');
    });

    it('handles API errors with custom error handler', async () => {
      const error = new Error('API Error');
      mockQontoClient.mockRejectedValue(error);

      const onError = vi.fn<(err: unknown) => void>();
      await fetchReceivableInvoicesUpload(uploadId, { onError });

      expect(onError).toHaveBeenCalledWith(error);
    });

    it('throws error when no error handler is provided', async () => {
      const error = new Error('API Error');
      mockQontoClient.mockRejectedValue(error);

      await expect(fetchReceivableInvoicesUpload(uploadId)).rejects.toThrow('API Error');
    });

    it('throws error when upload not found', async () => {
      const error = new Error('Not Found');
      mockQontoClient.mockRejectedValue(error);

      await expect(fetchReceivableInvoicesUpload('non-existent-id')).rejects.toThrow('Not Found');
    });
  });

  describe(useFetchReceivableInvoicesUpload, () => {
    it('handles custom error callback', async () => {
      const error = new Error('API Error');
      mockQontoClient.mockRejectedValue(error);

      const queryClient = new QueryClient();
      const wrapper = ({ children }: { children: React.ReactNode }) => {
        return createElement(QueryClientProvider, { client: queryClient }, children);
      };

      const onError = vi.fn();
      const { result } = renderHook(() => useFetchReceivableInvoicesUpload(uploadId, { onError }), {
        wrapper,
      });

      // Wait for the query to execute and fail
      await waitFor(() => {
        expect(result.current.isError).toBe(true);
      });

      expect(onError).toHaveBeenCalledWith(error);
    });
  });
});
